import type { Queue, QueuedDeliveryContext, QueuedEvent } from "./queue";
import { requiresConsentRevocationBinding } from "./shared-consent";
import type { TransportResult } from "./transport";

const MAX_DELIVERY_FAILURES = 2;

export function resolvePluginTransportKind(options: {
  isMarketing: boolean;
  isTrustedEssential: boolean;
}): "essential" | "marketing" | "analytics" {
  if (options.isMarketing) return "marketing";
  return options.isTrustedEssential ? "essential" : "analytics";
}

export function isRetryableTransportFailure(result: TransportResult) {
  if (result.status === undefined) return true;
  return (
    result.status === 408 ||
    result.status === 425 ||
    result.status === 429 ||
    result.status >= 500
  );
}

export function stageDeliveryAttempt(queue: Queue, events: QueuedEvent[]) {
  events.forEach((event) => queue.enqueue(event));
}

export function retainOnlyEssentialQueuedEvents(
  queue: Queue,
  isEssential: (event: QueuedEvent) => boolean,
) {
  queue.remove(
    queue
      .peek()
      .filter((event) => !isEssential(event))
      .map((event) => event.eventId),
  );
}

export function isExplicitDenialQueuedEvent(event: QueuedEvent) {
  const consent = event.delivery?.consent;
  if (
    event.type !== "consent_update" ||
    event.delivery?.deliveryClass !== "essential" ||
    !consent
  ) {
    return false;
  }
  return requiresConsentRevocationBinding(consent);
}

/** Only the bounded, identifier-free answer lane survives opt-out. */
export function isAnonymousConsentResponseQueuedEvent(event: QueuedEvent) {
  const delivery = event.delivery;
  const props = event.props;
  return (
    event.type === "consent_banner_interaction" &&
    delivery?.deliveryClass === "essential" &&
    !delivery.visitor &&
    !delivery.context &&
    !delivery.revocation &&
    !delivery.revocationToken &&
    !delivery.consent.cmp &&
    delivery.policy.firstPartyCookies === false &&
    props?.response_version === 1 &&
    typeof props.choice === "string" &&
    ["denied", "analytics", "personalization"].includes(props.choice) &&
    typeof props.consent_mode === "string" &&
    ["required", "preview"].includes(props.consent_mode) &&
    typeof props.cmp_source === "string" &&
    ["prospix_ui", "marketing_banner_v1"].includes(props.cmp_source) &&
    props.action === (props.choice === "denied" ? "reject" : "accept") &&
    Object.keys(props).every((key) =>
      [
        "response_version",
        "choice",
        "consent_mode",
        "cmp_source",
        "action",
      ].includes(key),
    )
  );
}

export function isPrivacyEssentialQueuedEvent(event: QueuedEvent) {
  return (
    isExplicitDenialQueuedEvent(event) ||
    isAnonymousConsentResponseQueuedEvent(event)
  );
}

export type QueuedFirstPartyCookieTarget = {
  apiBase: string;
  siteKey: string;
};

export function queuedFirstPartyCookieTarget(
  event: QueuedEvent,
): QueuedFirstPartyCookieTarget | undefined {
  const delivery = event.delivery;
  if (
    !delivery ||
    delivery.policy.firstPartyCookies === false ||
    !delivery.apiBase ||
    !delivery.siteKey
  ) {
    return undefined;
  }
  return { apiBase: delivery.apiBase, siteKey: delivery.siteKey };
}

export function queuedFirstPartyCookieTargetKey(
  target: QueuedFirstPartyCookieTarget,
) {
  return `${target.apiBase.replace(/\/+$/, "")}\n${target.siteKey}`;
}

export function shouldWithholdForPendingCookieRevoke(
  event: QueuedEvent,
  pendingTargetKeys: ReadonlySet<string>,
) {
  const target = queuedFirstPartyCookieTarget(event);
  return (
    !isExplicitDenialQueuedEvent(event) &&
    target !== undefined &&
    pendingTargetKeys.has(queuedFirstPartyCookieTargetKey(target))
  );
}

export function groupEventsByDeliveryContext(events: QueuedEvent[]) {
  const groups = new Map<
    string,
    { delivery: QueuedDeliveryContext; events: QueuedEvent[] }
  >();
  for (const event of events) {
    if (!event.delivery) continue;
    const key = JSON.stringify(event.delivery);
    const group = groups.get(key) ?? {
      delivery: event.delivery,
      events: [],
    };
    group.events.push(event);
    groups.set(key, group);
  }
  return Array.from(groups.values());
}

type ConsentBoundDeliveryState = {
  isEssential: boolean;
  currentlyAllowed: boolean;
  stagedRevocationGeneration: number;
  currentRevocationGeneration: number;
  stagedSharedRevocationToken?: string | null;
  currentSharedRevocationToken?: string | null;
};

export function isConsentBoundDeliveryCurrent(
  options: ConsentBoundDeliveryState,
) {
  if (!options.currentlyAllowed) return false;
  return (
    options.isEssential ||
    (options.stagedRevocationGeneration ===
      options.currentRevocationGeneration &&
      options.stagedSharedRevocationToken ===
        options.currentSharedRevocationToken)
  );
}

export function shouldRetainAfterConsentChange(
  options: ConsentBoundDeliveryState,
) {
  return isConsentBoundDeliveryCurrent(options);
}

export function settleDeliveryAttempt(
  queue: Queue,
  events: QueuedEvent[],
  result: TransportResult,
  options: { shouldRetain?: (event: QueuedEvent) => boolean } = {},
) {
  if (result.beaconAccepted) return;

  if (result.ok) {
    queue.remove(events.map((event) => event.eventId));
    return;
  }

  const retryable = isRetryableTransportFailure(result);
  events.forEach((event) => {
    if (
      (options.shouldRetain && !options.shouldRetain(event)) ||
      (!retryable && event.attempts >= MAX_DELIVERY_FAILURES)
    ) {
      queue.remove([event.eventId]);
      return;
    }
    queue.enqueue({
      ...event,
      attempts: Math.min(event.attempts + 1, Number.MAX_SAFE_INTEGER),
    });
  });
}
